import {
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from "solid-js";
import SiteAssetsContext from "~/components/shared/SiteAssets/SiteAssetsContext";

function Inputs(props: {
  onThemeChange: (arg0: string) => void;
  theme: string;
}) {
  const context = useContext(SiteAssetsContext);

  if (!context) {
    throw new Error(
      "SiteAssetsContext is undefined. This usually means you are trying to use context outside of the provider. Make sure your component is wrapped with the SiteAssetsContext.Provider.",
    );
  }

  const handleThemeChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    props.onThemeChange(target.value);
  };

  return (
    <form
      id="theme-switcher"
      class="text-sm isolate inline-flex"
      aria-label="Theme switcher"
    >
      <div class="relative inline-flex  px-3 py-1 mr-2">
        <input
          type="radio"
          id="auto"
          name="theme"
          value="auto"
          class="mr-2"
          onChange={handleThemeChange}
          checked={props.theme === "auto"}
        />
        <label for="auto" aria-label="Auto theme">
          Auto
        </label>
      </div>
      <div class="relative inline-flex items-center px-3 py-1 mr-2">
        <input
          type="radio"
          id="light"
          name="theme"
          value="light"
          class="mr-2"
          onChange={handleThemeChange}
          checked={props.theme === "light"}
        />
        <label for="light" aria-label="Light theme">
          Light
        </label>
      </div>
      <div class="relative inline-flex items-center px-3 py-1">
        <input
          type="radio"
          id="dark"
          name="theme"
          value="dark"
          class="mr-2"
          onChange={handleThemeChange}
          checked={props.theme === "dark"}
        />
        <label for="dark" aria-label="Dark theme">
          Dark
        </label>
      </div>
    </form>
  );
}

function ThemeSwitcher() {
  const [theme, setTheme] = createSignal(
    localStorage.getItem("theme") || "auto",
  );
  let mediaMatch: MediaQueryList;

  onMount(() => {
    findTheme();
    registerMediaListener();
  });

  createEffect(() => {
    setDocTheme(theme());
  });

  onCleanup(() => {
    unregisterMediaListener();
  });

  const setDocTheme = (theme: string) => {
    document.documentElement.setAttribute("color-scheme", theme);
    updateLocalStorage("theme", theme);
  };

  function findTheme() {
    const storedTheme = localStorage.getItem("theme");
    if (storedTheme) {
      setTheme(storedTheme);
    } else if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      setTheme("dark");
    } else {
      setTheme("light");
    }
  }

  function updateLocalStorage(key: string, newValue: string) {
    try {
      localStorage.setItem(key, newValue);
    } catch (e) {
      console.error("LocalStorage is not available");
    }
  }

  const handleToggleClick = (newTheme: string) => {
    setTheme(newTheme);
    setDocTheme(newTheme);
  };

  function registerMediaListener() {
    mediaMatch = window.matchMedia("(prefers-color-scheme: light)");
    mediaMatch.addEventListener("change", onLightSchemeChange);
  }

  function unregisterMediaListener() {
    if (mediaMatch)
      mediaMatch.removeEventListener("change", onLightSchemeChange);
  }

  function onLightSchemeChange(e: MediaQueryListEvent) {
    if (e.matches) {
      setTheme("light");
      setDocTheme("light");
    } else {
      setTheme("dark");
      setDocTheme("dark");
    }
  }

  return (
    <div class="align-middle">
      <Inputs theme={theme()} onThemeChange={handleToggleClick} />
    </div>
  );
}

export default ThemeSwitcher;
